import type { QuoteRequestForm } from '@repo/api-contracts';
import { ChevronDown } from 'lucide-react';
import { type ReactElement, useId } from 'react';
import { cn, Label } from '@/shared/ui';

export interface PrintMethodFieldProps {
  /** 선택한 인쇄 방식 id, 미선택이면 ''. */
  readonly value: string;
  readonly methods: QuoteRequestForm['printMethods'];
  readonly error?: string | undefined;
  readonly onChange: (printMethodId: string) => void;
}

/**
 * 인쇄 방식 선택(견적 설계 §5-4). 하나뿐일 때의 미리 선택은 폼이 초기값으로 한다.
 * 입력칸과 같은 알약 모양·44px 높이 — 기본 화살표를 지우고 같은 자리에 가는 화살표를 그린다.
 */
export const PrintMethodField = ({
  value,
  methods,
  error,
  onChange,
}: PrintMethodFieldProps): ReactElement => {
  const id = useId();
  const errorId = `${id}-error`;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} isRequired>
        인쇄 방식
      </Label>
      <div className="relative">
        <select
          id={id}
          required
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={cn(
            'flex h-11 w-full cursor-pointer appearance-none rounded-full border border-input bg-background pr-11 pl-4 text-sm transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:cursor-not-allowed disabled:opacity-50',
            value === '' && 'text-muted-foreground',
            error && 'border-destructive focus-visible:ring-destructive',
          )}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">인쇄 방식을 선택하세요</option>
          {methods.map((method) => (
            <option key={method.id} value={method.id} className="text-foreground">
              {method.name}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden
          strokeWidth={1.5}
          className="pointer-events-none absolute top-1/2 right-4 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </div>
      {error ? (
        <p id={errorId} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
};
